import { PDFArray, PDFRef, PDFCheckBox, PDFDocument, PDFDropdown, PDFField, PDFName, PDFOptionList, PDFRadioGroup, PDFTextField, PDFHexString, PDFBool, PDFDict, StandardFonts } from "pdf-lib";
import { drawTextAnywhere, measureTextAnywhere, needsPicture } from "./drawTextAnywhere";

/**
 * pdf-lib draws form-field text with a standard font that only knows Western European letters, so one
 * answer in Hindi, Arabic, Chinese, Cyrillic ... used to make saving the whole form fail. Fields whose text
 * the standard font cannot draw get their box appearance drawn by the browser instead (a small picture of
 * the text, which every PDF reader shows), and the real text is stored as the field's value.
 */

export interface FieldFailure { name: string; reason: string }

/** Draws `text` into every box of the text field as a picture-based appearance and stores it as the value. */
export async function setTextFieldWithPicture(pdf: PDFDocument, field: PDFTextField, text: string): Promise<void> {
  field.acroField.setValue(PDFHexString.fromText(text));
  const multiline = field.isMultiline();
  for (const w of field.acroField.getWidgets()) {
    const r = w.getRectangle();
    const w0 = Math.max(10, r.width), h0 = Math.max(8, r.height);
    const lines = multiline ? text.split(/\r?\n/) : [text.replace(/[\r\n]+/g, " ")];
    let size = multiline ? 11 : Math.min(14, Math.max(6, h0 * 0.62));
    if (!multiline) {
      const wd = await measureTextAnywhere(pdf, lines[0], size);
      if (wd > w0 - 4) size = Math.max(5, (size * (w0 - 4)) / wd);
    }
    const tmp = pdf.addPage([w0, h0]);
    try {
      let y = multiline ? h0 - size - 2 : (h0 - size) / 2 + size * 0.22;
      for (const line of lines) {
        if (line) await drawTextAnywhere(pdf, tmp, line, { x: 2, y, size });
        y -= size * 1.2;
      }
      const emb = await pdf.embedPage(tmp);
      w.setNormalAppearance(emb.ref);
    } finally {
      pdf.removePage(pdf.getPageCount() - 1);
    }
  }
  await pdf.flush(); // makes the new appearance a real stream now, so "needs update" checks and flattening see it
}

/** Brings every field's drawn appearance up to date. A field that fails is reported, never skipped silently. */
export async function refreshAppearances(pdf: PDFDocument): Promise<FieldFailure[]> {
  const form = pdf.getForm();
  const helv = await pdf.embedFont(StandardFonts.Helvetica);
  const failed: FieldFailure[] = [];
  // Forms saved with "NeedAppearances" ask the reader to redraw every field; a field's stored drawing can then
  // be stale or empty even though pdf-lib thinks it is up to date, so redraw all text and choice fields.
  const needAll = form.acroForm.dict.lookup(PDFName.of("NeedAppearances")) === PDFBool.True;
  for (const field of form.getFields()) {
    try {
      const textual = field instanceof PDFTextField || field instanceof PDFDropdown || field instanceof PDFOptionList;
      if (!(needAll && textual) && !field.needsAppearancesUpdate?.()) continue;
      if (field instanceof PDFTextField) {
        const v = field.getText() ?? "";
        if (needsPicture(v)) await setTextFieldWithPicture(pdf, field, v);
        else field.defaultUpdateAppearances(helv);
      } else if (field instanceof PDFDropdown || field instanceof PDFOptionList) {
        field.defaultUpdateAppearances(helv);
      } else {
        (field as PDFCheckBox | PDFRadioGroup).defaultUpdateAppearances();
      }
    } catch (err) {
      failed.push({ name: field.getName(), reason: String((err as Error)?.message ?? err).slice(0, 120) });
    }
  }
  return failed;
}

/** Turns off "redraw on open" so readers show the appearances prepared here. */
export function appearancesAreFinal(pdf: PDFDocument): void {
  const acro = pdf.catalog.lookupMaybe(PDFName.of("AcroForm"), PDFDict);
  if (acro) acro.set(PDFName.of("NeedAppearances"), PDFBool.False);
}

export type { PDFField };


/**
 * After flattening, a page's /Annots can still list widgets that were removed, which some readers
 * (poppler, Preview) report as "Invalid XRef entry". Drop every annotation reference that no longer resolves.
 */
export function dropDeadAnnotations(pdf: PDFDocument): void {
  for (const page of pdf.getPages()) {
    const annots = page.node.lookupMaybe(PDFName.of("Annots"), PDFArray);
    if (!annots) continue;
    for (let i = annots.size() - 1; i >= 0; i--) {
      const item = annots.get(i);
      if (item instanceof PDFRef && !pdf.context.lookup(item)) annots.remove(i);
    }
    if (annots.size() === 0) page.node.delete(PDFName.of("Annots"));
  }
}
